import { useEffect } from 'react';

/**
 * SEOHead Component
 * Manages document head elements for SEO including title, meta tags, and structured data
 */
const SEOHead = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  structuredData,
  keywords,
  noIndex = false
}) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper to update or create meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Update meta description
    if (description) {
      setMetaTag('description', description);
      setMetaTag('og:description', description, true);
      setMetaTag('twitter:description', description);
    }

    // Update keywords meta tag
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Update Open Graph tags
    if (title) {
      setMetaTag('og:title', title, true);
      setMetaTag('twitter:title', title);
    }

    if (ogType) {
      setMetaTag('og:type', ogType, true);
    }

    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
      setMetaTag('twitter:image', ogImage);
      setMetaTag('twitter:card', 'summary_large_image');
    }

    if (canonicalUrl) {
      setMetaTag('og:url', canonicalUrl, true);

      // Update canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);

      // Update hreflang tags for the current page path
      const pagePath = canonicalUrl.replace(/https?:\/\/[^/]+/, '');
      const updateHreflang = (hreflang, baseUrl) => {
        let link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'alternate');
          link.setAttribute('hreflang', hreflang);
          document.head.appendChild(link);
        }
        link.setAttribute('href', `${baseUrl}${pagePath}`);
      };

      updateHreflang('en-IL', 'https://www.happyhourstlv.com');
      updateHreflang('en-GB', 'https://www.happyhourslondon.com');
      updateHreflang('x-default', 'https://www.happyhourstlv.com');
    }

    // Handle noIndex
    if (noIndex) {
      setMetaTag('robots', 'noindex, nofollow');
    }

    // Add structured data (JSON-LD) - supports single object or array of schemas
    if (structuredData) {
      // Remove existing structured data scripts if present
      const existingScripts = document.querySelectorAll('script[data-seo-head]');
      existingScripts.forEach(script => script.remove());

      const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
      schemas.forEach((schema, index) => {
        if (schema) {
          const script = document.createElement('script');
          script.setAttribute('type', 'application/ld+json');
          script.setAttribute('data-seo-head', `true-${index}`);
          script.textContent = JSON.stringify(schema);
          document.head.appendChild(script);
        }
      });
    }

    // Cleanup function
    return () => {
      // Remove structured data scripts on unmount
      const scripts = document.querySelectorAll('script[data-seo-head^="true"]');
      scripts.forEach(script => script.remove());
    };
  }, [title, description, canonicalUrl, ogImage, ogType, structuredData, keywords, noIndex]);

  return null;
};

/**
 * Generate LocalBusiness structured data for a venue
 */
export const generateVenueStructuredData = (venue, city) => {
  if (!venue) return null;

  const cityName = city?.name || 'Tel Aviv';
  const countryCode = city?.country || 'IL';

  return {
    '@context': 'https://schema.org',
    '@type': 'BarOrPub',
    name: venue.headline,
    description: venue.main_offer || `Happy hour deals at ${venue.headline}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: venue.address,
      addressLocality: cityName,
      addressCountry: countryCode
    },
    ...(venue.lat && venue.lon && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: venue.lat,
        longitude: venue.lon
      }
    }),
    ...(venue.phone && { telephone: venue.phone }),
    ...(venue.link && { url: venue.link }),
    ...(venue.image_url && venue.image_url !== 'none' && { image: venue.image_url }),
    ...(venue.opening_hours && {
      openingHoursSpecification: generateOpeningHours(venue.opening_hours)
    }),
    priceRange: '$$',
    servesCuisine: 'Bar Food',
    areaServed: cityName
  };
};

/**
 * Convert opening_hours object to schema.org format
 */
const generateOpeningHours = (openingHours) => {
  const dayMap = {
    sunday: 'Sunday',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday'
  };

  const specs = [];

  Object.entries(openingHours).forEach(([day, slots]) => {
    if (slots && slots.length > 0) {
      slots.forEach(slot => {
        if (slot && slot.start && slot.end) {
          const opens = `${Math.floor(slot.start / 100).toString().padStart(2, '0')}:${(slot.start % 100).toString().padStart(2, '0')}`;
          const closes = `${Math.floor(slot.end / 100).toString().padStart(2, '0')}:${(slot.end % 100).toString().padStart(2, '0')}`;

          specs.push({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: dayMap[day],
            opens,
            closes
          });
        }
      });
    }
  });

  return specs;
};

/**
 * Generate ItemList structured data for the homepage
 */
export const generateListStructuredData = (venues, city) => {
  if (!venues || venues.length === 0) return null;

  const cityName = city?.name || 'Tel Aviv';
  const siteUrl = city?.siteUrl || 'https://www.happyhourstlv.com';

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Happy Hour Deals in ${cityName}`,
    description: `Find the best happy hour deals at bars and restaurants in ${cityName}`,
    numberOfItems: venues.length,
    itemListElement: venues.slice(0, 10).map((venue, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${siteUrl}/venue/${venue.id}`,
      item: {
        '@type': 'BarOrPub',
        name: venue.headline,
        description: venue.main_offer,
        address: venue.address,
        ...(venue.image_url && venue.image_url !== 'none' && { image: venue.image_url })
      }
    }))
  };
};

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbStructuredData = (items, city) => {
  if (!items || items.length === 0) return null;

  const siteUrl = city?.siteUrl || window.location.origin;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url && { item: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}` })
    }))
  };
};

/**
 * Generate homepage structured data combining WebSite and ItemList
 */
export const generateHomepageStructuredData = (venues, city) => {
  const cityName = city?.name || 'Tel Aviv';
  const siteUrl = city?.siteUrl || 'https://www.happyhourstlv.com';
  const displayName = city?.displayName || 'Happy Hours Tel Aviv';

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: displayName,
      description: `Discover the best happy hour deals at bars and restaurants in ${cityName}`,
      url: siteUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: displayName,
      url: siteUrl,
      logo: `${siteUrl}/images/site_preview_image_en.jpg`,
      ...(city?.facebookUrl && { sameAs: [city.facebookUrl] }),
      ...(city?.contactEmail && { email: city.contactEmail })
    }
  ];

  // Add ItemList if venues are available
  if (venues && venues.length > 0) {
    schemas.push(generateListStructuredData(venues, city));
  }

  return schemas;
};

export default SEOHead;
